import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, Trash2, ArrowLeft, ExternalLink, Upload, BellRing, Loader2, KeyRound } from 'lucide-react';
import AdminResetPasswordDialog from '../components/AdminResetPasswordDialog';
import { toast } from 'sonner';
import { DriverInviteForm } from '../components/DriverInviteForm';
import { InviteShareActions, DeliveryChips } from '../components/InviteShareActions';
import { OnboardingFunnelBoard } from '../components/OnboardingFunnelBoard';
import DashboardLayout from '../components/DashboardLayout';
import BulkDriverImportModal from '../components/BulkDriverImportModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { track } from '../lib/telemetry';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  invited: 'bg-slate-100 text-slate-700',
  registered: 'bg-blue-100 text-blue-700',
  pending_approval: 'bg-orange-100 text-orange-700',
  active: 'bg-green-100 text-green-700',
};

const STATUS_LABELS = {
  invited: 'Invited',
  registered: 'Registered',
  pending_approval: 'Pending Approval',
  active: 'Active',
};

const DriversManagement = () => {
  const { user } = useAuth();
  const [resetDriver, setResetDriver] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || 'all';
  const highlightId = searchParams.get('highlight');
  const highlightRef = useRef(null);
  const [invitations, setInvitations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [remindersBusy, setRemindersBusy] = useState(false);
  const [funnelKey, setFunnelKey] = useState(0);

  const runReminders = async () => {
    setRemindersBusy(true);
    try {
      const res = await axios.post(`${API}/driver-invitations/scan-reminders`, {}, { withCredentials: true });
      const n = res.data.reminders_sent || 0;
      (n ? toast.success : toast.info)(n ? `${n} reminder${n === 1 ? '' : 's'} sent (${res.data.unopened_reminders || 0} unopened, ${res.data.expiry_reminders || 0} expiring)` : 'No reminders due right now');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not run reminders');
    } finally {
      setRemindersBusy(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invitesRes, driversRes] = await Promise.all([
        axios.get(`${API}/driver-invitations`, { withCredentials: true }),
        axios.get(`${API}/drivers`, { withCredentials: true }),
      ]);
      setInvitations(invitesRes.data);
      setDrivers(driversRes.data);
      setFunnelKey((k) => k + 1);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const visibleDrivers = useMemo(() => {
    if (statusFilter === 'all') return drivers;
    return drivers.filter(d => d.status === statusFilter);
  }, [drivers, statusFilter]);

  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, visibleDrivers]);

  const handleDeleteInvite = async (id) => {
    if (!window.confirm('Delete this invitation?')) return;
    try {
      await axios.delete(`${API}/driver-invitations/${id}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error deleting invitation:', error);
    }
  };

  const handleApprove = async (driverId) => {
    if (!window.confirm('Approve this driver and set status to Active?')) return;
    try {
      await axios.post(`${API}/drivers/${driverId}/approve`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to approve driver');
    }
  };

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8">
        <button
          data-testid="back-to-dashboard-button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back to Dashboard
        </button>

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Driver Management</h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Invite, onboard, and manage drivers for your company</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="bulk-import-drivers-btn"
              onClick={() => { setShowBulkImport(true); }}
              className="flex items-center gap-2 border border-slate-300 bg-white text-slate-700 px-4 py-2 rounded-md font-semibold hover:bg-slate-50 transition-all duration-200"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <Upload className="w-4 h-4" strokeWidth={2} />
              Bulk Import
            </button>
            <button
              data-testid="invite-driver-button"
              onClick={() => { track('click', 'driver-invite-open'); setShowInviteForm(!showInviteForm); }}
              className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <UserPlus className="w-4 h-4" strokeWidth={2} />
              Invite Driver
            </button>
          </div>
        </div>

        <BulkDriverImportModal
          open={showBulkImport}
          onClose={() => setShowBulkImport(false)}
          onImported={fetchData}
        />

        {showInviteForm && (
          <DriverInviteForm companyName={user?.company_name} senderName={user?.name} onInvited={() => fetchData()} />
        )}

        <OnboardingFunnelBoard companyName={user?.company_name} senderName={user?.name} refreshKey={funnelKey} />

        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Pending Invitations</h2>
              <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Drivers who haven't opened their link get an automatic re-send (email + text) after 2 days, up to 2 times.</p>
            </div>
            {invitations.some((i) => !i.used) && (
              <button
                type="button"
                data-testid="run-invite-reminders-button"
                onClick={runReminders}
                disabled={remindersBusy}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {remindersBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                Send due reminders now
              </button>
            )}
          </div>
          {invitations.filter(i => !i.used).length === 0 ? (
            <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No pending invitations.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Email</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Name</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Mobile</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Delivery</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Sent</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Expires</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Link</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.filter(i => !i.used).map((inv) => (
                    <tr key={inv.id} data-testid={`invitation-row-${inv.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{inv.email}</td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{inv.name || '-'}</td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{inv.phone || '-'}</td>
                      <td className="py-3 px-2"><DeliveryChips invite={inv} /></td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{new Date(inv.last_sent_at || inv.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{new Date(inv.expires_at).toLocaleDateString()}</td>
                      <td className="py-3 px-2">
                        <InviteShareActions invite={inv} companyName={user?.company_name} senderName={user?.name} onResent={() => fetchData()} compact />
                      </td>
                      <td className="py-3 px-2">
                        <button
                          data-testid={`delete-invite-${inv.id}`}
                          onClick={() => handleDeleteInvite(inv.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Drivers</h2>
            {statusFilter !== 'all' && (
              <button
                data-testid="clear-status-filter"
                onClick={() => navigate('/drivers')}
                className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Filtered by status: <span className="bg-[#002FA7]/10 px-2 py-0.5 rounded">{statusFilter}</span>
                <span className="text-slate-500">(clear)</span>
              </button>
            )}
          </div>
          {visibleDrivers.length === 0 ? (
            <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{drivers.length === 0 ? 'No drivers yet. Send an invitation to get started.' : 'No drivers match this filter.'}</p>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden space-y-3" data-testid="drivers-mobile-list">
                {visibleDrivers.map((d) => {
                  const isHighlighted = highlightId && (d.user_id === highlightId || d.id === highlightId);
                  return (
                    <div
                      key={d.id}
                      ref={isHighlighted ? highlightRef : null}
                      data-testid={`driver-card-${d.id}`}
                      className={`bg-white border rounded-lg p-4 ${isHighlighted ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>{d.name}</p>
                          <p className="text-xs text-slate-500 truncate mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{d.email}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${STATUS_COLORS[d.status] || 'bg-slate-100 text-slate-700'}`}>
                          {STATUS_LABELS[d.status] || d.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <span className="text-slate-400">Type:</span> {d.driver_type ? (d.driver_type === 'company_driver' ? 'Company Driver' : 'Owner Operator') : '—'}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`mobile-view-driver-${d.id}`}
                          onClick={() => navigate(`/drivers/${d.id}`)}
                          className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-semibold text-[#002FA7] bg-[#002FA7]/5 active:bg-[#002FA7]/10 rounded-md"
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        >
                          <ExternalLink className="w-4 h-4" /> View
                        </button>
                        {d.status === 'pending_approval' && (
                          <button
                            data-testid={`mobile-approve-driver-${d.id}`}
                            onClick={() => handleApprove(d.id)}
                            className="flex-1 min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-green-600 active:bg-green-700 rounded-md"
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                          >
                            Approve
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="overflow-x-auto hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Name</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Email</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Type</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Status</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDrivers.map((d) => {
                    const isHighlighted = highlightId && (d.user_id === highlightId || d.id === highlightId);
                    return (
                    <tr
                      key={d.id}
                      ref={isHighlighted ? highlightRef : null}
                      data-testid={`driver-row-${d.id}`}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${isHighlighted ? 'bg-amber-50 ring-2 ring-[#FF9500]' : ''}`}
                    >
                      <td className="py-3 px-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{d.name}</td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{d.email}</td>
                      <td className="py-3 px-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {d.driver_type ? (d.driver_type === 'company_driver' ? 'Company Driver' : 'Owner Operator') : '-'}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        <span data-testid={`driver-status-${d.id}`} className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_COLORS[d.status] || 'bg-slate-100 text-slate-700'}`}>
                          {STATUS_LABELS[d.status] || d.status}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`view-driver-${d.id}`}
                            onClick={() => navigate(`/drivers/${d.id}`)}
                            className="text-xs text-[#002FA7] hover:text-[#00247D] flex items-center gap-1"
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                          >
                            <ExternalLink className="w-3 h-3" />
                            View
                          </button>
                          {['company_admin', 'super_admin'].includes(user?.role) && d.user_id && (
                            <button
                              data-testid={`reset-password-driver-${d.id}`}
                              onClick={() => setResetDriver({ id: d.user_id, name: d.name, email: d.email })}
                              className="text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1"
                              title="Reset password (temporary password)"
                              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                            >
                              <KeyRound className="w-3 h-3" />
                              Reset password
                            </button>
                          )}
                          {d.status === 'pending_approval' && (
                            <button
                              data-testid={`approve-driver-${d.id}`}
                              onClick={() => handleApprove(d.id)}
                              className="text-xs bg-green-600 text-white px-3 py-1 rounded font-semibold hover:bg-green-700 transition-all"
                              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                            >
                              Approve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>
      {resetDriver && <AdminResetPasswordDialog member={resetDriver} onClose={() => setResetDriver(null)} />}
    </DashboardLayout>
  );
};

export default DriversManagement;
